import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

export default function Register() {
  const { company, logoDark } = useCompany();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", username: "", password: "", phone: "",
    role: "client",
    num_accommodations: "",
    ref_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState({
    email_taken: false, phone_taken: false, username_taken: false,
  });
  const { register } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setForm((f) => ({ ...f, ref_code: ref.toUpperCase() }));
  }, [searchParams]);

  // Debounced precheck — runs 400ms after the user stops typing, hits
  // the dedicated endpoint and surfaces inline duplicate warnings.
  useEffect(() => {
    const handle = setTimeout(async () => {
      const payload = {};
      if (form.email && form.email.includes("@")) payload.email = form.email;
      if (form.username && form.username.length >= 3) payload.username = form.username;
      if (form.phone && form.phone.replace(/\D/g, "").length >= 6) payload.phone = form.phone;
      if (!Object.keys(payload).length) {
        setConflicts({ email_taken: false, phone_taken: false, username_taken: false });
        return;
      }
      try {
        const { data } = await api.post("/auth/register-precheck", payload);
        setConflicts(data);
      } catch (_) { /* silent */ }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.email, form.username, form.phone]);

  function update(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.username || !payload.username.trim()) delete payload.username;
      if (!payload.ref_code || !payload.ref_code.trim()) delete payload.ref_code;
      // Only send num_accommodations for client role — coerce to integer.
      if (payload.phone) {
        // Normalize: accept "912 345 678", "912345678", "+351 912 345 678" …
        let digits = payload.phone.replace(/[^\d+]/g, "");
        if (!digits.startsWith("+")) {
          const clean = digits.replace(/\D/g, "");
          if (clean.length === 9) digits = `+351${clean}`;
          else digits = clean;
        }
        payload.phone = digits;
      }
      if (payload.role === "client") {
        const n = parseInt(payload.num_accommodations, 10);
        payload.num_accommodations = Number.isFinite(n) && n >= 0 ? n : 0;
      } else {
        delete payload.num_accommodations;
      }
      const res = await register(payload);
      if (res.pending_approval) {
        toast.success("Conta criada. Aguarda aprovação do administrador.");
        nav("/login");
      } else {
        toast.success("Conta criada com sucesso");
        if (res.role === "client") {
          // If host declared any accommodations, go straight into onboarding
          // so they can register them (and then schedule cleanings).
          if ((res.num_accommodations || 0) > 0) {
            nav("/cliente/alojamentos?onboarding=1");
          } else {
            nav("/cliente");
          }
        } else {
          nav("/");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  const isClient = form.role === "client";

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-[#062c27]">
        <img alt="" src="https://images.pexels.com/photos/7546314/pexels-photo-7546314.jpeg"
             className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#041f1b]/70 to-[#041f1b]" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="inline-block">
            <span className="bg-[#fdfbf7] rounded-sm px-3 py-2 inline-flex items-center">
              <img src={logoDark} alt={company.trade_name} className="h-16 w-auto" />
            </span>
          </Link>
          <div>
            <div className="eyebrow mb-4">Junte-se à rede</div>
            <h1 className="font-display text-5xl leading-tight">Comece<br/>a agendar hoje.</h1>
          </div>
        </div>
      </div>

      <div className="p-8 md:p-16 flex items-center">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <Link to="/"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] transition-colors -mb-1"
                data-testid="register-back-home">
            <ArrowLeft size={14} /> Voltar à página inicial
          </Link>

          {/* Bonus announcement — visible only when user clicked
              «Quero aproveitar» on the home banner (URL carries ?bonus=<days>).
              The backend grants a N-day welcome promo to every new client
              automatically, so registering is all the user needs to do to
              unlock it. */}
          {searchParams.get("bonus") && (
            <div className="rounded-sm border border-[#d4af37]/50 bg-[#062c27] p-4 flex items-start gap-3"
                 data-testid="register-bonus-banner">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#d4af37] text-[#031411] shrink-0">
                <Sparkles size={16} />
              </span>
              <div>
                <div className="text-sm font-semibold text-[#fdfbf7]">
                  Promoção de {searchParams.get("bonus")} dias ativada
                </div>
                <div className="text-xs text-[#a0b2af] mt-1 leading-relaxed">
                  Ao criar a sua conta agora, o bónus é <strong className="text-[#d4af37]">ativado imediatamente</strong> e estará
                  visível no seu painel com a contagem decrescente.
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="eyebrow mb-2">Nova conta</div>
            <h2 className="font-display text-4xl">Criar conta</h2>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { id: "client", label: "Host / Cliente" },
              { id: "employee", label: "Funcionário" },
            ].map(r => (
              <button type="button" key={r.id} onClick={() => update("role", r.id)}
                      className={`py-3 text-sm border rounded-sm ${form.role === r.id ? "border-[#d4af37] text-[#d4af37]" : "border-[#1a4640] text-[#a0b2af]"}`}
                      data-testid={`role-${r.id}`}>
                {r.label}
              </button>
            ))}
          </div>

          {form.role === "employee" && (
            <div className="text-xs text-[#f59e0b] border border-[#f59e0b]/30 bg-[#f59e0b]/5 px-3 py-2 rounded-sm">
              Funcionários só podem entrar após aprovação do administrador.
            </div>
          )}

          <Input label="Nome completo" value={form.name} onChange={v => update("name", v)} testId="register-name" />
          <div className="space-y-1">
            <Input label="Email" type="email" value={form.email} onChange={v => update("email", v)} testId="register-email" />
            {conflicts.email_taken && (
              <div className="text-[11px] text-[#f59e0b]" data-testid="register-email-taken">
                Este email já está registado. <Link to="/login" className="underline">Entrar</Link> ou
                <Link to="/recuperar-password" className="underline ml-1">recuperar palavra-passe</Link>?
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Input label="Utilizador (opcional · permite entrar também com este nome em vez do email)"
                   value={form.username} onChange={v => update("username", v)} testId="register-username" required={false} />
            {conflicts.username_taken && (
              <div className="text-[11px] text-[#f59e0b]" data-testid="register-username-taken">
                Este utilizador já está em uso. Escolha outro nome.
              </div>
            )}
          </div>
          <div className="space-y-1" data-testid="register-phone-block">
            <Input label="Telefone" type="tel" value={form.phone}
                   onChange={v => update("phone", v.replace(/[^\d\s+]/g, ""))}
                   testId="register-phone" />
            <div className="text-[11px] text-[#a0b2af]">
              Não precisa de +351 — adicionamos automaticamente se não escrever.
            </div>
            {conflicts.phone_taken && (
              <div className="text-[11px] text-[#f59e0b]" data-testid="register-phone-taken">
                Este número já está associado a outra conta.
              </div>
            )}
          </div>
          <Input label="Palavra-passe" type="password" value={form.password} onChange={v => update("password", v)} testId="register-password" />

          {isClient && (
            <div className="space-y-2" data-testid="register-num-accommodations-block">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">
                Quantos alojamentos disponíveis para limpezas?
              </label>
              <input
                type="number" min={0} max={50} inputMode="numeric"
                required
                value={form.num_accommodations}
                onChange={e => update("num_accommodations", e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="Ex: 3"
                className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37]"
                data-testid="register-num-accommodations" />
              <div className="text-[11px] text-[#a0b2af]">
                Pode adicionar, editar ou remover alojamentos a qualquer momento na sua área.
              </div>
            </div>
          )}

          {isClient && (
            <div className="space-y-2" data-testid="register-ref-code-block">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">
                Código de indicação (opcional)
              </label>
              <input
                type="text"
                value={form.ref_code}
                onChange={e => update("ref_code", e.target.value.toUpperCase())}
                placeholder="LUX-XXXXXX"
                className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37] font-mono tracking-wider"
                data-testid="register-ref-code" />
              <div className="text-[11px] text-[#a0b2af]">
                {form.ref_code
                  ? <>A usar o código <span className="text-[#d4af37] font-mono">{form.ref_code}</span>. O seu amigo ganha 5€ de desconto quando efetuar o primeiro pagamento.</>
                  : "Se um amigo o recomendou, introduza aqui o código dele."}
              </div>
            </div>
          )}

          <button disabled={busy} className="btn-gold w-full py-4 text-base disabled:opacity-60" data-testid="register-submit">
            {busy ? "A criar…" : "Criar conta"}
          </button>
          <div className="text-sm text-[#a0b2af]">
            Já tem conta? <Link to="/login" className="text-[#d4af37] hover:underline">Entrar</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, required = true }) {
  const inputMode = type === "email" ? "email" : type === "tel" ? "tel" : undefined;
  const autoCap = type === "email" || type === "password" ? "none" : undefined;
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-widest text-[#a0b2af]">{label}</label>
      <input type={type} required={required} value={value} onChange={e => onChange(e.target.value)}
             inputMode={inputMode} autoCapitalize={autoCap} autoCorrect={autoCap ? "off" : undefined}
             className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-base outline-none focus:border-[#d4af37]"
             data-testid={testId} />
    </div>
  );
}
